import React, { useState, useMemo } from "react";
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Radio,
  TextField,
  Box,
} from "@mui/material";

const GlobalDropdown = ({
  id,
  name,
  value,
  onChange,
  errorMessage,
  onBlur,
  placeholder = "Select",
  options = [],
  multiple = false,
  counts = {},
  variant = "citySelect",
  renderValueFn,
  error,
  renderMenuItem,
  menuProps = {},
  search = false,
  disabled = false,
  useNewStyles = false,
  height = "32px",

  menuMaxHeight = 300,

  menuWidth = 150,

  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);

  const ORIGINAL_STYLES = {
    searchDropdown: {
      style: {
        width: "90%",
        fontSize: "12px",
        color: "#1f2937",
        backgroundColor: "transparent",
        fontWeight: 400,
        outline: "none",
        border: "none",
        borderBottom: "1px solid #57595b",
        padding: "5px 0px",
      },
    },
    multiSelect: {
      sx: {
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#b0b0b0" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#b0b0b0",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
      },
    },
    citySelect: {
      style: { color: "#a2a2a2" },
      sx: {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "rgba(59, 130, 246, 1)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
      },
    },
    enquirySelect: {
      sx: {
        color: "var(--label-color)",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "rgba(59, 130, 246, 1)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
        "& .MuiOutlinedInput-root": {
          "&:hover fieldset": {
            borderColor: error ? "var(--error-color)" : "#b0b0b0",
          },
        },
      },
    },
  };

  const NEW_STYLES = {
    stateSelect: {
      style: { color: "#a2a2a2" },
      sx: {
        width: "10rem",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "rgba(59, 130, 246, 1)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
      },
    },
    compact: {
      sx: {
        width: "100%",
        maxWidth: "100%",
        fontSize: "12px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
          borderWidth: "1px",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "rgba(59, 130, 246, 1)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },
      },
    },
  };

  const DROPDOWN_STYLE_MAP = useNewStyles
    ? {
        ...ORIGINAL_STYLES,
        ...NEW_STYLES,
      }
    : ORIGINAL_STYLES;

  const variantStyles = DROPDOWN_STYLE_MAP[variant] || {};
  const inlineStyle = variantStyles.style || {};
  const sxStyle = variantStyles.sx || {};

  const filteredOptions = useMemo(() => {
    if (!search || !searchTerm.trim()) {
      return options;
    }

    const searchValue = searchTerm.trim().toLowerCase();

    return options.filter((opt) =>
      String(opt.label || "")
        .toLowerCase()
        .includes(searchValue),
    );
  }, [options, searchTerm, search]);

  const normalizedValue = useMemo(() => {
    if (multiple) {
      return Array.isArray(value) ? value.map((v) => String(v)) : [];
    }

    return value === undefined || value === null ? "" : String(value);
  }, [multiple, value]);

  /* ---------------------------------------
     SELECT CLOSE
  --------------------------------------- */

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
    // onClose?.();
  };

  const handleChange = (event) => {
    onChange?.(event);

    if (multiple) {
      setOpen(true);
    }
  };

  return (
    <Select
      id={id}
      name={name}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      multiple={multiple}
      displayEmpty
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      inputProps={{
        "aria-label": placeholder,
      }}
      style={inlineStyle}
      sx={{
        ...sxStyle,

        height: height,
        minHeight: height,
        "& .MuiSelect-select": {
          height: `${parseInt(height, 10) - 2}px`,
          minHeight: `${parseInt(height, 10) - 2}px`,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          padding: "0 30px 0 8px !important",

          fontSize: "12px",
          lineHeight: "normal",
        },

        /* Arrow vertically center */

        "& .MuiSelect-icon": {
          top: "50%",
          transform: "translateY(-50%)",
          right: "6px",
        },

        /* Border */

        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#b0b0b0",
        },

        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "#9ca3af",
        },

        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: error ? "var(--error-color)" : "rgba(59, 130, 246, 1)",
        },
      }}
      renderValue={(selected) => {
        const isEmpty =
          selected === null ||
          selected === undefined ||
          (Array.isArray(selected) && selected.length === 0);

        if (isEmpty) {
          return (
            <span style={{ color: "var(--listdown-color)" }}>
              {placeholder}
            </span>
          );
        }

        return (
          <span
            style={{
              color: "var(--heading-color)",
              fontSize: "12px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              width: "100%",
            }}
          >
            {renderValueFn
              ? renderValueFn(selected)
              : multiple
                ? selected.join(", ")
                : selected}
          </span>
        );
      }}
      MenuProps={{
        disableAutoFocusItem: true,
        PaperProps: {
          sx: {
            width: menuWidth,
            maxWidth: menuWidth,

            maxHeight: menuMaxHeight,
            fontSize: "12px",

            overflowY: "auto",

            "& .MuiMenuItem-root": {
              minHeight: "34px",
              height: "34px",
              padding: "4px 8px",
              fontSize: "12px",
            },

            "& .MuiListItemText-root": {
              margin: 0,
            },

            "& .MuiListItemText-primary": {
              fontSize: "12px",
              lineHeight: 1.2,
            },

            "& .MuiCheckbox-root": {
              padding: "4px",
              marginRight: "4px",
            },

            "& .MuiRadio-root": {
              padding: "4px",
              marginRight: "4px",
            },

            /* Scrollbar */

            "&::-webkit-scrollbar": {
              width: "8px",
            },

            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#9ca3af",
              borderRadius: "8px",
            },

            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f3f4f6",
            },
          },

          // onMouseDown: (e) => {
          //   e.stopPropagation();
          // },
        },
        ...menuProps,
      }}
    >
      {search && (
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#fff",
            padding: "8px",
          }}
          onClickCapture={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            placeholder="Search..."
            autoFocus
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              "& .MuiInputBase-root": { height: "40px" },
              "& input": { fontSize: "14px" },
            }}
          />
        </Box>
      )}

      {filteredOptions.map((opt) => {
        const stringOptValue = String(opt.value);

        const isSelected = multiple
          ? normalizedValue.includes(stringOptValue)
          : normalizedValue === stringOptValue;

        if (renderMenuItem) {
          return (
            <React.Fragment key={stringOptValue}>
              {renderMenuItem(opt)}
            </React.Fragment>
          );
        }

        return (
          <MenuItem key={stringOptValue} value={opt.value}>
            {multiple ? (
              <Checkbox checked={isSelected} size="small" />
            ) : (
              <Radio checked={isSelected} size="small" />
            )}

            <ListItemText
              primary={`${opt.label}${
                counts?.[opt.value] !== undefined
                  ? ` (${counts[opt.value]})`
                  : ""
              }`}
            />
          </MenuItem>
        );
      })}

      {search && filteredOptions.length === 0 && (
        <MenuItem disabled>No results found</MenuItem>
      )}
    </Select>
  );
};

export default GlobalDropdown;
